import React, { useMemo, useState, useEffect } from "react";
import { Row, Col, Tab, Tabs } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { ThreeCircles } from "react-loader-spinner";

import CygnusGrid from "../../reusable_components/CygnusGrid/CygnusGrid";
import RulesModal from "../RulesModal/RulesModal";
import CustomButton from "../../reusable_components/custom_button_component/Button";

import {
    getAllFields,
} from "../../client_onboarding/MDMFieldsConfig/MDMFieldsConfig.service";

import { fetchFirmRules, saveFirmRules, fetchRuleApproverPrivileges } from "../RulesModal/RulesModal.service";
import { SelectedFieldOptionType } from "../RulesModal/RulesModal.types";
import { ApproverFieldOption } from "../../../Reusables/components/ClientApproverSetting/ClientApproverSetting.utils";
import { BtnCellRenderer, ListRenderer, ExecutionModeRenderer, handleRowReorder } from "./ApprovalConfig.utils";
import { Rule, ApprovalConfigProps, SYSTEM_FIELDS, EL_SYSTEM_FIELDS } from "./ApprovalConfig.types";
import { ColDef } from "ag-grid-community";
import styles from "./ApprovalConfig.module.css";
import { TimeRenderer } from "../../client_onboarding/ClientsGrid/ClientsGrid.utils";

const ApprovalConfig: React.FC<ApprovalConfigProps> = () => {
  const [rowData, setRowData] = useState<Rule[]>([]);
  const [selectedRowData, setSelectedRowData] = useState<Rule>();
  const [elRowData, setElRowData] = useState<Rule[]>([]);
  const [addRulesModalShow, setAddRulesModalShow] = useState(false);
  const [ruleName, setRuleName] = useState("");
  const [ruleId, setRuleId] = useState<number | undefined>();
  const [ruleType, setRuleType] = useState("client");
  const [selectedFieldsList, setSelectedFieldsList] = useState<SelectedFieldOptionType[]>([]);
  const [staffApproverFieldsList, setStaffApproverFieldsList] = useState<SelectedFieldOptionType[]>([]);
  const [ruleApproverOptions, setRuleApproverOptions] = useState<ApproverFieldOption[]>([]);
  const [clientKey, setClientKey] = useState<number>();
  const [elKey, setElKey] = useState<number>();
  const [refreshGrid, setRefreshGrid] = useState(false);

  const [activeTab, setActiveTab] = useState("client");

  const handleSelect = (key: string | null) => {
    if (!key) return;
    setActiveTab(key);
  };

  useEffect(() => {
    const init = async () => {
        const fields = await getAllFields();
        const selectedFieldData: SelectedFieldOptionType[] = fields.map(item => ({
            id: item.field_code,
            label: item.field_name,
            value: item.field_code
        }));
        setSelectedFieldsList([...selectedFieldData, ...SYSTEM_FIELDS]);

        const staffFields = await getAllFields("PARTNER_INFO");
        const staffApproverFieldData: SelectedFieldOptionType[] = staffFields
          .filter((item) => (item.field_code || "").toLowerCase().endsWith("_email"))
          .map((item) => ({
            id: item.field_code,
            label: (item.field_name || "").replace(/email/i, "").replace(/\s{2,}/g, " ").trim(),
            value: item.field_code,
          }));
        setStaffApproverFieldsList(staffApproverFieldData);

        const ruleOptions = await fetchRuleApproverPrivileges();
        if (ruleOptions.length > 0) {
          setRuleApproverOptions(ruleOptions.map((item) => ({ ...item, id: item.value })));
        }
    };
    init();
  }, []);

  const loadRules = async () => {
    setRefreshGrid(true);
    try {
      const rules = await fetchFirmRules();
      const clientRules = rules.filter(
        (r) => (r.RuleType ?? "").toLowerCase() === "client"
      );
      const elRules = rules.filter(
        (r) => (r.RuleType ?? "").toLowerCase() === "el"
      );

      setClientKey(clientRules.length);
      setElKey(elRules.length);
      setRowData(clientRules);
      setElRowData(elRules);
    } catch (err) {
      console.error(err);
    } finally {
      setRefreshGrid(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Rule Name",
        field: "RuleName",
        flex: 1,
        autoHeight: true,
        wrapText: true,
        rowDrag: true,
      },
      {
        headerName: "Criteria",
        field: "Criteria",
        flex: 2,
        autoHeight: true,
        cellRenderer: ListRenderer
      },
      {
        headerName: "Approvers",
        field: "Approvers",
        flex: 1,
        autoHeight: true,
        cellRenderer: ListRenderer,
      },
      {
        headerName: "Execution Order",
        field: "ExecutionOrder",
        cellDataType: "text",
        flex: 1,
        minWidth: 110,
      },
      {
        headerName: "Execution Mode",
        field: "ExecutionMode",
        flex: 1,
        minWidth: 110,
        cellRenderer: ExecutionModeRenderer,
      },
      {
        headerName: "Created By",
        field: "CreatedBy",
        flex: 1,
        minWidth: 130,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "Created Date",
        field: "CreatedDate",
        cellRenderer: TimeRenderer,
        flex: 1,
        minWidth: 170,
      },
      {
        headerName: "Updated Date",
        field: "UpdatedDate",
        cellRenderer: TimeRenderer,
        flex: 1,
        minWidth: 170,
      },
      {
        headerName: "Action",
        field: "",
        minWidth: 90,
        maxWidth: 90,
        filter: false,
        sortable: false,
        pinned: "right",
        cellRenderer: BtnCellRenderer(
          setSelectedRowData,
          setRuleType,
          setAddRulesModalShow,
          setRuleId,
          setRuleName,
          "client",
          loadRules
        ),
      },
    ],
    []
  );

  const elColumnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Rule Name",
        field: "RuleName",
        flex: 1,
        autoHeight: true,
        wrapText: true,
        rowDrag: true,
      },
      {
        headerName: "Criteria",
        field: "Criteria",
        flex: 2,
        autoHeight: true,
        cellRenderer: ListRenderer
      },
      {
        headerName: "Approvers",
        field: "Approvers",
        flex: 1,
        autoHeight: true,
        cellRenderer: ListRenderer
      },
      {
        headerName: "Execution Order",
        field: "ExecutionOrder",
        flex: 1,
        minWidth: 110,
      },
      {
        headerName: "Execution Mode",
        field: "ExecutionMode",
        flex: 1,
        minWidth: 110,
        cellRenderer: ExecutionModeRenderer,
      },
      {
        headerName: "Created By",
        field: "CreatedBy",
        flex: 1,
        minWidth: 130,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "Created Date",
        field: "CreatedDate",
        cellRenderer: TimeRenderer,
        flex: 1,
        minWidth: 170,
      },
      {
        headerName: "Updated Date",
        field: "UpdatedDate",
        cellRenderer: TimeRenderer,
        flex: 1,
        minWidth: 170,
      },
      {
        headerName: "Action",
        field: "",
        minWidth: 90,
        maxWidth: 90,
        filter: false,
        sortable: false,
        pinned: "right",
        cellRenderer: BtnCellRenderer(
          setSelectedRowData,
          setRuleType,
          setAddRulesModalShow,
          setRuleId,
          setRuleName,
          "el",
          loadRules
        ),
      },
    ],
    []
  );

  return (
    <>
      {addRulesModalShow && (
        <RulesModal
          selectedRowData={selectedRowData}
          ruleName={ruleName}
          ruleType={ruleType}
          ruleId={ruleId}
          selectedFieldsList={
            ruleType === "el"
              ? [...selectedFieldsList, ...EL_SYSTEM_FIELDS]
              : selectedFieldsList
          }
          staffApproverFieldsList={staffApproverFieldsList}
          ruleApproverOptions={ruleApproverOptions}
          show={addRulesModalShow}
          onHide={(shouldRefresh) => {
            setAddRulesModalShow(false);
            if(shouldRefresh) {
              loadRules();
            }
          }}
        />
      )}

      <Row className="no-gutter page-wrapper">
        <p className={`display-6 ${styles.pageTitle}`}>
          Approval Configuration
        </p>
      </Row>

      <Row className="no-gutter">
        <Col md={12} className={`d-flex justify-content-end ${styles.actionRow}`}>
          <CustomButton
            onclick={() => {
              setRuleName("");
              setRuleId(undefined);
              setSelectedRowData(undefined);
              setRuleType(activeTab);
              setAddRulesModalShow(true);
            }}
            content="Create Rule"
          />
          <div className={styles.reloadWrapper}>
            {refreshGrid ? (
              <ThreeCircles
                height="20"
                width="20"
                color="#4fa94d"
                visible={true}
                ariaLabel="three-circles-rotating"
                outerCircleColor="var(--theme1_tertiary)"
                innerCircleColor="var(--theme1_secondary)"
                middleCircleColor="var(--theme1_primary)"
              />
            ) : (
              <Icon.ArrowCounterclockwise
                onClick={() => loadRules()}
                className={styles.reloadIcon}
              />
            )}
          </div>
        </Col>
      </Row>

      <Tabs activeKey={activeTab} onSelect={handleSelect}>
        <Tab eventKey="client" title="Client Approval">
          <Row style={{ height: "calc(95vh - 100px)" }}>
            <Col md={12} className={styles.approvalGrid}>
              <CygnusGrid
                key={clientKey}
                fetchData={async () => ({
                  records: rowData,
                  totalCount: rowData.length,
                })}
                dataIdentifier="RuleId"
                cachedData={rowData}
                setCachedData={setRowData}
                cachedIsLoading={refreshGrid}
                columnDefs={columnDefs}
                rowSelection={undefined}
                rowModelType="clientSide"
                enableRowReorder={true}
                onRowReorder={(event: any) => handleRowReorder(event, setRowData)}
              />
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="el" title="EL Approval">
          <Row style={{ height: "calc(95vh - 100px)" }}>
            <Col md={12} className={styles.approvalGrid}>
              <CygnusGrid
                key={elKey}
                fetchData={async () => ({
                  records: elRowData,
                  totalCount: elRowData.length,
                })}
                dataIdentifier="RuleId"
                cachedIsLoading={refreshGrid}
                cachedData={elRowData}
                setCachedData={setElRowData}
                columnDefs={elColumnDefs}
                rowSelection={undefined}
                rowModelType="clientSide"
                enableRowReorder={true}
                onRowReorder={(event: any) => handleRowReorder(event, setElRowData)}
              />
            </Col>
          </Row>
        </Tab>
      </Tabs>
    </>
  );
};

export default ApprovalConfig;
